/** Rozdělení sloupce umístění z DOCCC: budova, místnost, přiřazený uživatel. */
export type ParsedItAssetLocation = {
  building: string
  room: string
  user: string
  raw: string
}

/** Kódy budov z DOCCC → zkratka areálu. */
const BUILDING_LABELS: Record<string, string> = {
  '1': 'OB-6',
  '2': 'D-8',
  '3': 'OB-26',
  '4': 'NH-1',
  '5': 'M-68',
  '6': 'K-29',
  '8': 'Křižovnická 4',
  '9': 'Podhradská',
  '1B': 'H-17',
}

export function buildingDisplayLabel(code: string): string {
  return BUILDING_LABELS[code] ?? code
}

function buildingMatchesQuery(code: string, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const label = buildingDisplayLabel(code).toLowerCase()
  return code.toLowerCase().includes(q) || label.includes(q)
}

const NON_USER_LOCATIONS = new Set([
  'viz příloha',
  'autoškola',
  'studijní odd.',
  'dm pro žáky',
  '3x učeb.nh',
  'sklad it',
])

const ROOM_PATTERN = /^(\d+[A-Za-z]?)\s*[-–]\s*(.+)$/
const L_PREFIX_PATTERN = /^L\s*[-–]\s*(.+)$/i
const USER_PATTERN = /^[\p{L}\p{M}.,\s'-]+$/u

function isAssignedUser(value: string): boolean {
  const lower = value.toLowerCase()
  if (NON_USER_LOCATIONS.has(lower)) return false
  if (/^\d/.test(value) || /\d/.test(value)) return false
  return USER_PATTERN.test(value)
}

export function parseItAssetLocation(raw?: string | null): ParsedItAssetLocation {
  const rawStr = (raw || '').trim()
  if (!rawStr) {
    return { building: '', room: '', user: '', raw: '' }
  }

  const roomMatch = rawStr.match(ROOM_PATTERN)
  if (roomMatch) {
    return {
      building: buildingDisplayLabel(roomMatch[1]),
      room: roomMatch[2].trim(),
      user: '',
      raw: rawStr,
    }
  }

  const lMatch = rawStr.match(L_PREFIX_PATTERN)
  if (lMatch) {
    return {
      building: 'L',
      room: lMatch[1].trim(),
      user: '',
      raw: rawStr,
    }
  }

  if (isAssignedUser(rawStr)) {
    return { building: '', room: '', user: rawStr, raw: rawStr }
  }

  if (rawStr.toLowerCase() === 'viz příloha') {
    return { building: '', room: 'Příloha (Excel)', user: '', raw: rawStr }
  }

  return { building: '', room: rawStr, user: '', raw: rawStr }
}

export function buildingOptionsFromItems(
  items: { location?: string | null }[],
): string[] {
  const set = new Set<string>()
  for (const item of items) {
    const { building } = parseItAssetLocation(item.location)
    if (building) set.add(building)
  }
  return [...set].sort((a, b) =>
    a.localeCompare(b, 'cs', { numeric: true, sensitivity: 'base' }),
  )
}

export function itemBuildingMatchesFilter(
  location: string | null | undefined,
  query: string,
): boolean {
  const rawStr = (location || '').trim()
  if (!rawStr) return false
  const roomMatch = rawStr.match(ROOM_PATTERN)
  if (roomMatch) return buildingMatchesQuery(roomMatch[1], query)
  const lMatch = rawStr.match(L_PREFIX_PATTERN)
  if (lMatch) return buildingMatchesQuery('L', query)
  return false
}

export function itemMatchesBuildingSelection(
  location: string | null | undefined,
  selectedBuildings: ReadonlySet<string>,
  buildingQuery: string,
): boolean {
  if (selectedBuildings.size > 0) {
    const { building } = parseItAssetLocation(location)
    return Boolean(building && selectedBuildings.has(building))
  }
  const q = buildingQuery.trim()
  if (q) return itemBuildingMatchesFilter(location, q)
  return true
}

export function roomOptionsFromItems(
  items: { location?: string | null }[],
  options?: {
    selectedBuildings?: ReadonlySet<string>
    buildingQuery?: string
  },
): string[] {
  const selectedBuildings = options?.selectedBuildings ?? new Set<string>()
  const buildingQuery = options?.buildingQuery ?? ''
  const filterByBuilding = selectedBuildings.size > 0 || buildingQuery.trim() !== ''

  const set = new Set<string>()
  for (const item of items) {
    if (
      filterByBuilding &&
      !itemMatchesBuildingSelection(item.location, selectedBuildings, buildingQuery)
    ) {
      continue
    }
    const { room } = parseItAssetLocation(item.location)
    if (room) set.add(room)
  }
  return [...set].sort((a, b) =>
    a.localeCompare(b, 'cs', { numeric: true, sensitivity: 'base' }),
  )
}

export function userOptionsFromItems(items: { location?: string | null }[]): string[] {
  const set = new Set<string>()
  for (const item of items) {
    const { user } = parseItAssetLocation(item.location)
    if (user) set.add(user)
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'cs', { sensitivity: 'base' }))
}
